/** @format */

import {useEffect, useMemo, useRef, useState} from 'react';

import {KeyboardArrowDown} from '@mui/icons-material';
import {Box} from '@mui/material';
import {Trans} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router-dom';

import TertiarySelect from 'components/selects/TertiarySelect';

import SidebarEdit from 'components/MediaplanSidebar/SidebarEdit';
import SidebarNew from 'components/MediaplanSidebar/SidebarNew';
import Snackbar from 'components/Snackbar';

import {YalperAnalyticAttributionModelEnum} from 'graphql/generated/types';

import ErrorBlock from 'components/ErrorBlock';

import GoalPicker, {GoalPickerGoal} from '../../components/GoalPicker';

import {ATTRIBUTION_MODELS} from '../Periods/components/KpiPlanForm/constants';
import Banner from './components/Banner';
import SelectedGoal from './components/SelectedGoal';
import {
  useGetFunnelGoalsQuery,
  useGetFunnelSettingsQuery,
  useSaveFunnelSettingsMutation,
} from './graphql/__generated__';
import {GoalsFromQuery} from './interfaces';
import {
  AddLevelButton,
  BackButton,
  Footer,
  Header,
  LevelsContainer,
  SaveButton,
  StaticLevel,
  StyledButton,
  Subtitle,
  Title,
} from './styles';

const Funnel = () => {
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('companyId') || '';
  const isEdit = searchParams.get('edit');

  const navigate = useNavigate();

  const {data: goalsData, loading: goalsLoading} = useGetFunnelGoalsQuery({
    variables: {companyId},
    skip: !companyId,
  });

  const {data: settingsData, loading: settingsLoading} = useGetFunnelSettingsQuery();
  const [saveFunnelSettings, {loading: saving}] = useSaveFunnelSettingsMutation();

  const allGoals = useMemo(
    () => goalsData?.currentUser?.currentCompany?.analyticGoals || [],
    [goalsData],
  );

  const [selectedGoals, setSelectedGoals] = useState<GoalsFromQuery>([]);
  const [attributionModel, setAttributionModel] = useState<YalperAnalyticAttributionModelEnum>();
  const [attributionModelError, setAttributionModelError] = useState(false);

  useEffect(() => {
    const funnelSettings = settingsData?.currentUser?.currentCompany?.funnelSettings;

    if (funnelSettings) {
      setSelectedGoals(funnelSettings.goals || []);
      setAttributionModel(funnelSettings.attributionModel || '');
    }
  }, [settingsData]);

  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const availableForSelect = useMemo(() => {
    const notChosenIds = new Set(selectedGoals.map(g => g.id));
    return allGoals.filter(g => !notChosenIds.has(g.id));
  }, [selectedGoals, allGoals]);

  const isEmptyList = selectedGoals.length === 0 && availableForSelect.length === 0;

  const handleSave = async () => {
    if (!companyId) return;

    if (!attributionModel) {
      setAttributionModelError(true);
      return;
    }

    await saveFunnelSettings({
      variables: {
        companyId,
        settings: {
          goals: selectedGoals.map(g => ({
            id: Number(g.id),
            name: g.name,
            counterId: Number(g.counterId),
            counterName: g.counterName,
          })),
          attributionModel: attributionModel,
        },
      },
    });

    setSnackbarOpen(true);
  };

  const handleGoToProjectSettings = () => {
    navigate(`/home/project/edit?companyId=${companyId}&edit=1`);
  };

  const handleBack = () => {
    navigate('/home/main/status');
  };

  const handleAddGoal = (goal: GoalPickerGoal) => {
    setSelectedGoals(prev => [...prev, goal]);
    setMenuOpen(false);
  };

  return (
    <>
      {isEdit ? (
        <SidebarEdit activeStep={'FUNNEL'} enableAll />
      ) : (
        <SidebarNew activeStep={'FUNNEL'} enableAll />
      )}
      <Header>
        <Box data-testid='type'>
          <Title>
            <Trans>Воронка</Trans>
          </Title>
        </Box>
        <Box sx={{mt: 2}}>
          <Subtitle>
            Выберите последовательность показателей воронки. Это нужно, чтобы построить графики
            воронки в Цифровом Слепке.
          </Subtitle>
        </Box>
      </Header>

      <Box sx={{display: 'flex', justifyContent: 'space-between', gap: '64px'}}>
        <Box sx={{minWidth: '400px'}}>
          <LevelsContainer>
            <StaticLevel>
              <Box>Показы</Box>
            </StaticLevel>
            <StaticLevel>
              <Box>Клики</Box>
            </StaticLevel>

            {selectedGoals.map((goal, index) => (
              <SelectedGoal
                key={goal.id}
                goal={goal}
                setSelectedGoals={setSelectedGoals}
                index={index}
                allAnalyticGoals={allGoals}
                isAnalyticGoalsLoaded={!goalsLoading}
              />
            ))}

            <AddLevelButton
              ref={anchorRef}
              disableRipple={isEmptyList}
              onClick={() => setMenuOpen(!isEmptyList)}
            >
              <Box>
                <Trans>Выбрать показатель</Trans>
              </Box>
              <KeyboardArrowDown sx={{opacity: 0.6}} />
            </AddLevelButton>
          </LevelsContainer>
          {isEmptyList && (
            <ErrorBlock
              title='Яндекс Метрика не подключена'
              description='Чтобы добавлять цели в воронку, подключите относящийся к проекту кабинет Яндекс Метрики'
              type='warning'
            >
              <Box>
                <StyledButton onClick={handleGoToProjectSettings} variant='contained'>
                  <Trans>Подключить</Trans>
                </StyledButton>
              </Box>
            </ErrorBlock>
          )}
          <Box sx={{mb: 12, mt: 4}}>
            <Subtitle>
              <Trans>Модель атрибуции</Trans>
            </Subtitle>
            <Box sx={{mt: 2, maxWidth: 420}}>
              <TertiarySelect
                aria-label='attribution-model'
                placeholder='Выберите значение'
                value={attributionModel ?? ''}
                options={ATTRIBUTION_MODELS.map(({label, value}) => ({
                  value,
                  text: label,
                }))}
                onSelect={value => {
                  setAttributionModel(value);
                  setAttributionModelError(false);
                }}
                error={attributionModelError}
                errorText={'Выберите модель атрибуции'}
              />
            </Box>
          </Box>
          <GoalPicker
            open={menuOpen}
            anchorEl={anchorRef.current}
            goals={availableForSelect as GoalPickerGoal[]}
            onSelect={handleAddGoal}
            onClose={() => setMenuOpen(false)}
          />
        </Box>
        <Box>
          <Banner />
        </Box>
      </Box>
      <Footer>
        <SaveButton onClick={handleSave} disabled={goalsLoading || settingsLoading || saving}>
          <Trans>{saving ? 'Сохранение...' : 'Сохранить изменения'}</Trans>
        </SaveButton>
        <BackButton onClick={handleBack}>
          <Trans>На страницу проекта</Trans>
        </BackButton>
      </Footer>
      <Snackbar
        isSnackbarOpen={snackbarOpen}
        message={'Настройки воронки сохранены'}
        handleSnackbarClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default Funnel;
